import { useCallback, useReducer } from 'react';

/**
 * Validation timing (piece 3, Text Input): a field validates on blur and on a
 * submit attempt, per keystroke only after it has been marked invalid, and
 * never on load. The Field Format Validators decide validity; this decides
 * when their answer shows.
 */
export interface FieldValidationState {
  /** Set the first time the field is found invalid; from then on it re-validates live. */
  marked: boolean;
  message: string | null;
}

export type FieldValidationEvent =
  | { type: 'change'; outcome: string | null }
  | { type: 'blur'; outcome: string | null }
  | { type: 'submit'; outcome: string | null };

export const initialFieldValidation: FieldValidationState = { marked: false, message: null };

export function fieldValidationReducer(state: FieldValidationState, event: FieldValidationEvent): FieldValidationState {
  switch (event.type) {
    case 'change':
      return state.marked ? { marked: true, message: event.outcome } : state;
    case 'blur':
    case 'submit':
      return { marked: state.marked || event.outcome !== null, message: event.outcome };
  }
}

export interface FieldValidationOptions<V> {
  /** Open already marked, as a field re-rendered after a submit attempt does; the message comes from the value given. */
  initiallyMarked?: boolean;
  value?: V;
}

export function useFieldValidation<V>(validate: (value: V) => string | null, options: FieldValidationOptions<V> = {}) {
  const [state, dispatch] = useReducer(fieldValidationReducer, options, (o) =>
    o.initiallyMarked && o.value !== undefined ? { marked: true, message: validate(o.value) } : initialFieldValidation,
  );
  const onChange = useCallback((value: V) => dispatch({ type: 'change', outcome: validate(value) }), [validate]);
  const onBlur = useCallback((value: V) => dispatch({ type: 'blur', outcome: validate(value) }), [validate]);
  const onSubmit = useCallback((value: V) => dispatch({ type: 'submit', outcome: validate(value) }), [validate]);
  return { message: state.message, marked: state.marked, onChange, onBlur, onSubmit };
}
